/* Scroll-snap carousel controller. Progressive enhancement over a natively
   scrollable track: adds prev/next arrows, page dots, end-of-track disabling.
   Hooks inside <scroll-carousel>: [data-carousel-viewport] track, [-prev]/[-next]
   arrows, [-dots] container (all optional). autoplay="<ms>" advances one page
   (off under prefers-reduced-motion; pauses on hover/focus-within/hidden tab).
   While paused it carries [data-paused] so CSS can hold the dot progress bar.
   [data-carousel-band], if present, takes the active slide's own background.
   loop: seamless wrap for one-slide-per-view tracks (see initLoop). */
// Several sections each link this file, so it can execute more than once per page:
// a top-level `class` declaration would throw on the second run.
if (!customElements.get('scroll-carousel')) customElements.define('scroll-carousel', class extends HTMLElement {
  connectedCallback() {
    this.vp = this.querySelector('[data-carousel-viewport]');
    if (!this.vp || this._ready) return;
    this._ready = true;

    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.dotsWrap = this.querySelector('[data-carousel-dots]');

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.page(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.page(1));
    this.vp.addEventListener('scroll', () => this.sync(), { passive: true });
    if (this.hasAttribute('loop') && this.vp.children.length > 1) this.initLoop();

    this._ro = new ResizeObserver(() => this.build());
    this._ro.observe(this.vp);
    this.build();

    const ms = parseInt(this.getAttribute('autoplay'), 10);
    if (ms > 0 && !this.reduce) this.autoplay(ms);
  }

  disconnectedCallback() {
    if (this._ro) this._ro.disconnect();
    if (this._timer) clearTimeout(this._timer);
    if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
  }

  autoplay(ms) {
    this._dwell = ms;
    // Two independent reasons to hold — the pointer or keyboard is inside the carousel,
    // and the tab is in the background. One flag each, so leaving one does not clear the
    // other. Reflected to the DOM, not just held on the instance: the dot progress bar is
    // a CSS animation, so pausing the timer without telling CSS would run the bar to full
    // while the slide sat still.
    const hold = (key, on) => {
      this['_hold_' + key] = on;
      const paused = !!(this._hold_hover || this._hold_hidden);
      if (paused === this._paused) return;
      this._paused = paused;
      this.toggleAttribute('data-paused', paused);
      if (paused) clearTimeout(this._timer);
      else this.restart();
    };
    this.addEventListener('pointerenter', () => hold('hover', true));
    this.addEventListener('pointerleave', () => hold('hover', false));
    this.addEventListener('focusin', () => hold('hover', true));
    this.addEventListener('focusout', () => hold('hover', false));
    this._onVis = () => hold('hidden', document.hidden);
    document.addEventListener('visibilitychange', this._onVis);
    this.restart();
  }

  /* The dwell begins when a slide ARRIVES, whoever moved it — autoplay, an arrow, a dot or
     a swipe. This used to be a setInterval keeping absolute cadence, which desynced from
     the progress bar three ways, all of them reported: a tick landing while paused was
     dropped rather than deferred, so the slide sat for up to twice the dwell; un-pausing
     mid-cycle fired almost immediately; and a manual scroll restarted the BAR — its
     animation is keyed to `.is-active`, so changing dots restarts it — without restarting
     the clock. sync() calls this whenever the active dot changes, so the bar and the timer
     now start from the same instant in every one of those cases. */
  restart() {
    if (!this._dwell || this._paused) return;
    clearTimeout(this._timer);
    // The advance re-arms the chain ITSELF, and does not wait to be re-armed by the
    // arrival it is about to cause. A setTimeout chain has a single point of failure a
    // setInterval never had: if one advance does not change the active page, sync() never
    // fires and there is no next timer — autoplay is dead for the rest of the visit. And
    // an advance failing is ordinary, not exotic: Chrome CANCELS an in-flight smooth
    // scroll the moment the visitor scrolls the page, which on a full-height hero is the
    // first thing they do. The arrival still calls restart() ~400ms later and supersedes
    // this timer, so the dwell is still measured from when the slide lands.
    this._timer = setTimeout(() => {
      this.page(1);
      this.restart();
    }, this._dwell);
  }

  /* Seamless loop: the last slide is cloned in front of the first and the first after the
     last, so a swipe past either end lands on a copy; once scrolling settles the track
     jumps, instantly and invisibly, to the real slide the copy stands for. Clones lose
     their ids and data-* hooks and go inert, so the lightbox, thumbnails and variant
     sync (which all find slides by data-media / data-lightbox-src) never see them.
     Assumes one slide per view, which is what a gallery is. */
  initLoop() {
    const vp = this.vp;
    // The slide on screen now, to stay on after the clones shift the track (a hidden
    // track measures 0 wide, so fall back to the first).
    const on = vp.children[Math.round(vp.scrollLeft / vp.clientWidth)] || vp.firstElementChild;
    const head = vp.lastElementChild.cloneNode(true);
    const tail = vp.firstElementChild.cloneNode(true);
    for (const c of [head, tail]) {
      for (const el of [c, ...c.querySelectorAll('[id]')]) el.removeAttribute('id');
      for (const a of [...c.attributes]) if (a.name.startsWith('data-')) c.removeAttribute(a.name);
      c.setAttribute('aria-hidden', 'true');
      c.inert = true;
    }
    vp.prepend(head);
    vp.append(tail);
    this.loop = true;
    this.jump(on);
    // scrollend waits for the finger to lift and momentum to stop; jumping mid-gesture
    // fights the touch on iOS. The settle timer is the backstop for engines without it
    // (and hidden tabs, which never fire it). wrap() is a no-op on a real slide, so
    // both firing is harmless.
    vp.addEventListener('scrollend', () => this.wrap());
    vp.addEventListener('scroll', () => {
      clearTimeout(this._settle);
      this._settle = setTimeout(() => this.wrap(), 300);
    }, { passive: true });
  }

  // Instant, never smooth: scrollTo's `behavior` beats any CSS scroll-behavior.
  jump(el, behavior = 'instant') {
    const left = this.vp.scrollLeft + el.getBoundingClientRect().left - this.vp.getBoundingClientRect().left;
    this.vp.scrollTo({ left, behavior });
  }

  wrap() {
    const kids = this.vp.children;
    const i = Math.round(this.vp.scrollLeft / this.vp.clientWidth);
    if (i === 0) this.jump(kids[kids.length - 2]);
    else if (i === kids.length - 1) this.jump(kids[1]);
  }

  get reduce() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  get scrollable() {
    return this.vp.scrollWidth - this.vp.clientWidth > 4;
  }
  get pages() {
    if (this.loop) return this.vp.children.length - 2;
    // Count in cells of (slide + gap), not raw widths. A gapped track measures
    // n*slide + (n-1)*gap, so scrollWidth / clientWidth overshoots by a fraction of
    // a gap and ceil() rounds it up to a phantom last page — a dot that scrolls the
    // track by a few pixels and looks broken. Adding one gap to both terms makes the
    // division exact for any slides-per-view. Ungapped tracks divide as before.
    const gap = parseFloat(getComputedStyle(this.vp).columnGap) || 0;
    return Math.max(1, Math.ceil((this.vp.scrollWidth + gap) / (this.vp.clientWidth + gap)));
  }

  page(dir) {
    if (this.loop) {
      this.vp.scrollBy({ left: dir * this.vp.clientWidth, behavior: this.reduce ? 'auto' : 'smooth' });
      return;
    }
    // Infinite wrap: past the last page loops to the first and vice-versa.
    const max = this.vp.scrollWidth - this.vp.clientWidth;
    const x = this.vp.scrollLeft;
    let left = x + dir * this.vp.clientWidth;
    if (dir > 0 && x >= max - 1) left = 0;
    else if (dir < 0 && x <= 1) left = max;
    this.vp.scrollTo({ left, behavior: this.reduce ? 'auto' : 'smooth' });
  }

  build() {
    // CSS hides arrows/dots when everything already fits.
    this.toggleAttribute('data-static', !this.scrollable);

    if (this.dotsWrap) {
      const n = this.pages;
      if (this.dotsWrap.childElementCount !== n) {
        this.dotsWrap.textContent = '';
        for (let i = 0; i < n; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
          dot.addEventListener('click', () => {
            if (this.loop) return this.jump(this.vp.children[i + 1], this.reduce ? 'auto' : 'smooth');
            const max = this.vp.scrollWidth - this.vp.clientWidth;
            const n = this.pages;
            const left = n > 1 ? (max * i) / (n - 1) : 0;
            this.vp.scrollTo({ left, behavior: this.reduce ? 'auto' : 'smooth' });
          });
          this.dotsWrap.appendChild(dot);
        }
      }
    }
    this.sync();
  }

  /* The dots band takes the ACTIVE slide's own ground: the nav is one element sitting
     outside a track whose slides each carry their own colour scheme, so it cannot inherit
     the colour the way a child would. Without JS the variable is never set and the band
     stays transparent — exactly the `under` row it is built on. One slide per page is
     assumed, which is what a hero is; a multi-up track paints from its first slide. */
  band(i) {
    const el = this.querySelector('[data-carousel-band]');
    const slide = el && this.vp.children[this.loop ? i + 1 : i];
    if (!slide) return;
    const bg = getComputedStyle(slide).getPropertyValue('--color-bg').trim();
    if (bg) el.style.setProperty('--cn-band-bg', bg);
  }

  sync() {
    // Arrows stay enabled — navigation wraps infinitely (see page()).
    // The active page is computed whether or not there are dots to paint: it is also what
    // restarts the dwell, and a carousel with dots turned off still autoplays.
    const max = this.vp.scrollWidth - this.vp.clientWidth;
    const n = this.dotsWrap && this.dotsWrap.children.length ? this.dotsWrap.children.length : this.pages;
    let active = max > 0 && n > 1 ? Math.round((this.vp.scrollLeft / max) * (n - 1)) : 0;
    // Loop: a clone counts as the slide it copies, so the dot does not flicker on the way.
    if (this.loop) active = (Math.round(this.vp.scrollLeft / this.vp.clientWidth) - 1 + n) % n;

    if (this.dotsWrap) {
      for (let i = 0; i < this.dotsWrap.children.length; i++) {
        this.dotsWrap.children[i].classList.toggle('is-active', i === active);
      }
    }

    if (active !== this._active) {
      this._active = active;
      this.band(active);
      this.restart();
    }
  }
});
